<template>
    <div
        class="reservation-chat-collapse"
        :class="{ 'reservation-chat-collapse--open': inlineOpen }"
        :aria-hidden="inlineOpen ? 'false' : 'true'"
        :inert="!inlineOpen"
    >
        <div class="reservation-chat-collapse-inner">
            <Teleport to="body" :disabled="!enlarged">
            <div
                class="reservation-chat-layer"
                :class="{ 'reservation-chat-layer--enlarged': enlarged }"
                @click.self="shrinkChat"
            >
            <div
                class="reservation-chat-panel rounded border"
                :class="enlarged ? 'overflow-hidden' : 'pa-3 mt-3'"
                @click.stop
            >
                <v-toolbar
                    v-if="enlarged"
                    color="primary"
                    density="compact"
                    class="flex-shrink-0"
                >
                    <v-row no-gutters align="center" class="flex-grow-1 px-5">
                        <v-col class="d-flex align-center min-w-0 py-0">
                            <v-toolbar-title class="text-yellow">
                                {{ $t("reservations.chat.title") }}
                            </v-toolbar-title>
                        </v-col>
                        <v-col v-if="reservationNumber" class="d-flex align-center justify-center min-w-0 py-0">
                            <v-tooltip v-if="entrySearchUrl" location="bottom" :z-index="4000">
                                <template #activator="{ props: entryLinkTooltipProps }">
                                    <router-link
                                        v-bind="entryLinkTooltipProps"
                                        :to="entrySearchUrl"
                                        class="reservation-chat-entry-link text-h6 font-weight-regular text-truncate"
                                        @click.stop
                                    >
                                        {{ reservationNumber }}
                                    </router-link>
                                </template>
                                {{ $t("reservations.viewOriginalEntry") }}
                            </v-tooltip>
                            <span v-else class="text-yellow text-h6 font-weight-regular text-truncate">
                                {{ reservationNumber }}
                            </span>
                        </v-col>
                        <v-col class="d-flex align-center justify-end py-0">
                        <v-tooltip location="bottom" :z-index="4000">
                            <template #activator="{ props: shrinkTooltipProps }">
                                <v-btn
                                    class="reservation-chat-tool-btn"
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="yellow"
                                    v-bind="shrinkTooltipProps"
                                    :aria-label="$t('reservations.chat.shrink')"
                                    @click="shrinkChat"
                                >
                                    <v-icon size="22">mdi-window-restore</v-icon>
                                </v-btn>
                            </template>
                            {{ $t("reservations.chat.shrink") }}
                        </v-tooltip>
                        <v-btn
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            color="yellow"
                            @click="closeChat"
                        >
                            <v-icon size="22">mdi-close</v-icon>
                        </v-btn>
                        </v-col>
                    </v-row>
                </v-toolbar>
                <div v-else class="d-flex align-center justify-space-between mb-2">
                    <span class="text-body-1 font-weight-bold">{{ $t("reservations.chat.title") }}</span>
                    <div class="d-flex align-center ga-1">
                        <v-btn
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            :aria-label="$t('reservations.chat.enlarge')"
                            @click="enlargeChat"
                        >
                            <v-icon size="22">mdi-window-restore</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                {{ $t("reservations.chat.enlarge") }}
                            </v-tooltip>
                        </v-btn>
                        <v-btn class="reservation-chat-tool-btn" icon size="x-small" variant="text" @click="closeChat">
                            <v-icon size="22">mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
                <div
                    ref="messagesEl"
                    v-auto-animate
                    class="reservation-chat-messages"
                    :class="enlarged ? 'flex-grow-1 overflow-y-auto min-h-0 px-3 pt-3' : 'mb-3'"
                >
                    <div v-if="loading" class="text-caption text-medium-emphasis px-1">
                        {{ $t("reservations.chat.loading") }}
                    </div>
                    <div v-else-if="!visibleMessages.length" class="text-caption text-medium-emphasis px-1">
                        {{ filesOnly ? $t("reservations.chat.noFiles") : $t("reservations.chat.empty") }}
                    </div>
                    <div
                        v-for="msg in visibleMessages"
                        :key="msg.uid"
                        class="reservation-chat-row"
                        :class="{ 'reservation-chat-row--mine': isMine(msg) }"
                    >
                        <div
                            v-auto-animate
                            class="reservation-chat-bubble"
                            :class="{ 'reservation-chat-bubble--mine': isMine(msg) }"
                        >
                            <div v-if="msg.body && !filesOnly" class="reservation-chat-body">{{ msg.body }}</div>
                            <div v-if="messageAttachments(msg).length" class="reservation-chat-files">
                                <button
                                    v-for="(file, index) in messageAttachments(msg)"
                                    :key="`${msg.uid}-${index}`"
                                    type="button"
                                    class="reservation-chat-file"
                                    :title="file.name"
                                    @click="downloadAttachment(file)"
                                >
                                    <v-icon size="small" class="flex-shrink-0">mdi-file-document-outline</v-icon>
                                    <span class="reservation-chat-file-name">{{ file.name }}</span>
                                </button>
                            </div>
                            <div class="reservation-chat-meta">
                                <span class="reservation-chat-meta-who">{{ senderShortLabel(msg) }}</span>
                                <span class="reservation-chat-meta-sep" aria-hidden="true">·</span>
                                <span>{{ formatTime(msg.created_at) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div :class="enlarged ? 'flex-shrink-0 bg-surface px-3 pb-3 pt-2' : ''">
                <div class="d-flex ga-2 align-center">
                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        :aria-label="$t('reservations.chat.attach')"
                        @click="$refs.fileInput.click()"
                    >
                        <v-icon>mdi-paperclip</v-icon>
                        <v-tooltip activator="parent" location="top" :z-index="4000" max-width="280">
                            {{ $t("reservations.chat.attachHint") }}
                        </v-tooltip>
                    </v-btn>
                    <v-btn
                        icon
                        size="small"
                        :variant="filesOnly ? 'flat' : 'text'"
                        :color="filesOnly ? 'primary' : undefined"
                        :aria-pressed="filesOnly ? 'true' : 'false'"
                        :aria-label="$t('reservations.chat.showOnlyMedia')"
                        @click="filesOnly = !filesOnly"
                    >
                        <v-icon :class="{ 'text-yellow': filesOnly }">mdi-file-document-outline</v-icon>
                        <v-tooltip activator="parent" location="top" :z-index="4000">
                            {{ $t("reservations.chat.showOnlyMedia") }}
                        </v-tooltip>
                    </v-btn>
                    <input
                        ref="fileInput"
                        type="file"
                        class="d-none"
                        multiple
                        accept=".pdf,.doc,.jpg,.jpeg,.png,application/pdf,application/msword,image/jpeg,image/png"
                        @change="onFilePicked"
                    />
                    <v-textarea
                        class="flex-grow-1"
                        ref="draftField"
                        v-model="draft"
                        :placeholder="$t('reservations.chat.placeholder')"
                        rows="1"
                        max-rows="3"
                        auto-grow
                        hide-details
                        density="compact"
                        variant="outlined"
                        @keydown.enter.exact.prevent="send"
                    />
                    <v-btn
                        color="primary"
                        size="small"
                        :loading="sending"
                        :disabled="!canSend"
                        @click="send"
                    >
                        {{ $t("reservations.chat.send") }}
                    </v-btn>
                </div>
                <div v-if="selectedFiles.length" class="reservation-chat-chips mt-2">
                    <div
                        v-for="(file, index) in selectedFiles"
                        :key="`${file.name}-${index}`"
                        class="reservation-chat-chip"
                    >
                        <v-icon size="small" class="flex-shrink-0">mdi-file-document-outline</v-icon>
                        <span class="reservation-chat-chip-name" :title="file.name">{{ file.name }}</span>
                        <button
                            type="button"
                            class="reservation-chat-chip-remove"
                            :aria-label="$t('reservations.chat.removeFile')"
                            @click="removeFile(index)"
                        >
                            <v-icon size="14">mdi-close</v-icon>
                        </button>
                    </div>
                </div>
                <div v-if="error" class="text-caption text-error mt-1">{{ error }}</div>
                </div>
            </div>
            </div>
            </Teleport>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import emitter from "@/eventBus";
import { formatLocalTime } from "@/utils";

const CHAT_MAX = 2000;
const FILE_MAX = 5 * 1024 * 1024;
const FILE_MAX_COUNT = 2;
const FILE_EXT = new Set(["pdf", "doc", "jpg", "jpeg", "png"]);

export default {
    name: "ReservationChatPanel",
    props: {
        reservationUid: { type: String, required: true },
        unreadCount: { type: Number, default: 0 },
        expanded: { type: Boolean, default: false },
        reservationNumber: { type: String, default: "" },
        entrySearchUrl: { type: Object, default: null },
    },
    emits: ["read", "sent", "update:expanded"],
    data() {
        return {
            loading: false,
            sending: false,
            messages: [],
            draft: "",
            selectedFiles: [],
            enlarged: false,
            filesOnly: false,
            inlineOpen: false,
            layoutToken: 0,
            error: null,
            socket: null,
            currentUserId: null,
        };
    },
    computed: {
        canSend() {
            const text = this.draft.trim();
            const hasFile = this.selectedFiles.length > 0;
            if (this.sending) return false;
            if (!text && !hasFile) return false;
            return text.length <= CHAT_MAX;
        },
        visibleMessages() {
            if (!this.filesOnly) return this.messages;
            return this.messages.filter((msg) => this.messageAttachments(msg).length > 0);
        },
    },
    watch: {
        expanded(open) {
            if (open) {
                this.inlineOpen = true;
                this.openPanel();
            } else {
                this.layoutToken += 1;
                this.inlineOpen = false;
                this.enlarged = false;
                this.closeSocket();
            }
        },
        enlarged(open) {
            document.body.style.overflow = open ? "hidden" : "";
            if (open) this.$nextTick(() => this.scrollToBottom());
        },
        reservationUid() {
            this.messages = [];
            this.filesOnly = false;
            this.layoutToken += 1;
            this.enlarged = false;
            this.inlineOpen = this.expanded;
            if (this.expanded) {
                this.openPanel();
            }
        },
    },
    created() {
        try {
            this.currentUserId = JSON.parse(sessionStorage.getItem("user") || "{}")?.id ?? null;
        } catch {
            this.currentUserId = null;
        }
    },
    mounted() {
        window.addEventListener("keydown", this.onEscape);
    },
    beforeUnmount() {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", this.onEscape);
        this.closeSocket();
    },
    methods: {
        wait(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        collapseDuration() {
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0;
            return 300;
        },
        async enlargeChat() {
            if (this.enlarged) return;
            const token = ++this.layoutToken;
            this.inlineOpen = false;
            await this.wait(this.collapseDuration());
            if (token !== this.layoutToken || !this.expanded) return;
            this.enlarged = true;
        },
        async shrinkChat() {
            if (!this.enlarged) return;
            const token = ++this.layoutToken;
            this.enlarged = false;
            await this.$nextTick();
            await this.wait(this.collapseDuration() ? 40 : 0);
            if (token !== this.layoutToken || !this.expanded) return;
            this.inlineOpen = true;
        },
        closeChat() {
            this.layoutToken += 1;
            this.inlineOpen = false;
            this.enlarged = false;
            this.$emit("update:expanded", false);
        },
        onEscape(event) {
            if (event.key === "Escape" && this.enlarged) {
                this.shrinkChat();
            }
        },
        isMine(msg) {
            return msg?.sender?.id != null && msg.sender.id === this.currentUserId;
        },
        senderShortLabel(msg) {
            if (this.isMine(msg)) {
                return this.$t("reservations.chat.you");
            }
            const first = (msg.sender?.first_name || "").trim();
            const company = (msg.sender_company?.name || "").trim();
            if (first && company) return `${first} · ${company}`;
            return first || company || "—";
        },
        formatTime(iso) {
            if (!iso) return "";
            return formatLocalTime(iso, "HH:mm, DD.MM.YY");
        },
        focusDraft() {
            this.$nextTick(() => {
                const field = this.$refs.draftField;
                if (!field) return;
                const textarea =
                    field.$el?.querySelector?.("textarea") ||
                    (field.tagName === "TEXTAREA" ? field : null);
                if (textarea && typeof textarea.focus === "function") {
                    try {
                        textarea.focus({ preventScroll: true });
                    } catch {
                        textarea.focus();
                    }
                    return;
                }
                if (typeof field.focus === "function") {
                    field.focus();
                }
            });
        },
        async openPanel() {
            await this.fetchMessages();
            await this.markRead();
            this.connectSocket();
            this.$nextTick(() => {
                this.scrollToBottom();
                this.focusDraft();
            });
        },
        async fetchMessages() {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.get(
                    `/reservations/${this.reservationUid}/chat/messages/`,
                    { params: { limit: 50 } }
                );
                this.messages = data.results || [];
            } catch (e) {
                this.error = e.response?.data?.error || this.$t("reservations.chat.loadError");
            } finally {
                this.loading = false;
            }
        },
        async markRead() {
            try {
                await api.post(`/reservations/${this.reservationUid}/chat/read/`);
                this.$emit("read", this.reservationUid);
                emitter.emit("refresh-chat-unread");
            } catch {
                /* ignore */
            }
        },
        messageAttachments(msg) {
            if (Array.isArray(msg?.attachments)) return msg.attachments;
            return msg?.attachment ? [msg.attachment] : [];
        },
        onFilePicked(event) {
            const picked = Array.from(event.target.files || []);
            event.target.value = "";
            if (!picked.length) return;
            const next = this.selectedFiles.slice();
            let error = null;
            for (const file of picked) {
                const ext = (file.name.split(".").pop() || "").toLowerCase();
                if (!FILE_EXT.has(ext)) {
                    error = this.$t("reservations.chat.fileType");
                    continue;
                }
                if (file.size > FILE_MAX) {
                    error = this.$t("reservations.chat.fileTooLarge");
                    continue;
                }
                if (next.length >= FILE_MAX_COUNT) {
                    error = this.$t("reservations.chat.fileLimit");
                    break;
                }
                next.push(file);
            }
            this.selectedFiles = next;
            this.error = error;
        },
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        async downloadAttachment(file) {
            if (!file?.url) return;
            try {
                const response = await api.get(file.url, { responseType: "blob" });
                const objectUrl = URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = file.name || "download";
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(objectUrl);
            } catch (e) {
                this.error = e.response?.data?.error || this.$t("reservations.chat.sendError");
            }
        },
        async send() {
            if (!this.canSend) return;
            const body = this.draft.trim();
            const files = this.selectedFiles.slice();
            this.sending = true;
            this.error = null;
            try {
                let data;
                if (files.length) {
                    const form = new FormData();
                    form.append("body", body);
                    files.forEach((file) => form.append("file", file));
                    ({ data } = await api.post(
                        `/reservations/${this.reservationUid}/chat/messages/`,
                        form
                    ));
                } else {
                    ({ data } = await api.post(
                        `/reservations/${this.reservationUid}/chat/messages/`,
                        { body }
                    ));
                }
                this.draft = "";
                this.selectedFiles = [];
                if (!this.messages.some((m) => m.uid === data.uid)) {
                    this.messages.push(data);
                }
                this.$emit("sent", data);
                this.$nextTick(() => {
                    this.scrollToBottom();
                    this.focusDraft();
                });
            } catch (e) {
                this.error = e.response?.data?.error || this.$t("reservations.chat.sendError");
            } finally {
                this.sending = false;
                this.focusDraft();
            }
        },
        appendMessage(msg) {
            if (!msg?.uid) return;
            if (this.messages.some((m) => m.uid === msg.uid)) return;
            this.messages.push(msg);
            this.$nextTick(() => this.scrollToBottom());
            if (this.expanded && !this.isMine(msg)) {
                this.markRead();
            }
        },
        async connectSocket() {
            this.closeSocket();
            let ticket;
            try {
                const { data } = await api.post(
                    `/reservations/${this.reservationUid}/chat/ws-ticket/`
                );
                ticket = data?.ticket;
            } catch {
                return;
            }
            if (!ticket) return;
            const base = process.env.VUE_APP_API_URL.replace(/^(https?):\/\//, (_, proto) =>
                proto === "https" ? "wss://" : "ws://"
            ).replace(/\/api\/?$/, "");
            const wsUrl = `${base}/ws/chat/${this.reservationUid}/?ticket=${encodeURIComponent(ticket)}`;
            try {
                this.socket = new WebSocket(wsUrl);
                this.socket.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === "chat_message" && data.message) {
                            this.appendMessage(data.message);
                        }
                    } catch {
                        /* ignore */
                    }
                };
            } catch {
                /* ignore */
            }
        },
        closeSocket() {
            if (this.socket) {
                try {
                    this.socket.close();
                } catch {
                    /* ignore */
                }
                this.socket = null;
            }
        },
        scrollToBottom() {
            const el = this.$refs.messagesEl;
            if (el) el.scrollTop = el.scrollHeight;
        },
    },
};
</script>

<style scoped>
.reservation-chat-entry-link {
    color: #ffeb3b;
    text-decoration: none;
}
.reservation-chat-entry-link:hover {
    color: inherit;
    text-decoration: none;
}
.reservation-chat-collapse {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.28s ease;
}
.reservation-chat-collapse--open {
    grid-template-rows: 1fr;
}
.reservation-chat-collapse-inner {
    overflow: hidden;
    min-height: 0;
}
.reservation-chat-tool-btn {
    width: 28px;
    height: 28px;
}
.reservation-chat-layer--enlarged {
    position: fixed;
    inset: 0;
    z-index: 2500;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    background: rgba(15, 23, 42, 0.48);
    animation: reservation-chat-layer-in 0.18s ease;
}
@keyframes reservation-chat-layer-in {
    from { opacity: 0; }
    to { opacity: 1; }
}
.reservation-chat-layer--enlarged .reservation-chat-panel {
    width: min(880px, 100%);
    height: min(78vh, 720px);
    max-height: calc(100vh - 64px);
    margin: 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
    opacity: 1;
    transform: none;
}
.reservation-chat-layer--enlarged .reservation-chat-messages {
    flex: 1 1 auto;
    max-height: none;
    min-height: 0;
}
.reservation-chat-panel {
    width: 100%;
    background: rgba(var(--v-theme-surface), 1);
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.22s ease, transform 0.28s ease;
}
.reservation-chat-collapse--open .reservation-chat-panel {
    opacity: 1;
    transform: translateY(0);
}
.reservation-chat-messages {
    max-height: 180px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.reservation-chat-row {
    display: flex;
    justify-content: flex-start;
    min-width: 0;
    max-width: 100%;
}
.reservation-chat-row--mine {
    justify-content: flex-end;
}
.reservation-chat-bubble {
    max-width: 100%;
    min-width: 0;
    padding: 8px 12px 6px;
    border-radius: 14px 14px 14px 4px;
    background: rgba(var(--v-theme-on-surface), 0.06);
}
.reservation-chat-bubble--mine {
    border-radius: 14px 14px 4px 14px;
    background: rgba(var(--v-theme-primary), 0.16);
}
.reservation-chat-body {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.925rem;
    line-height: 1.35;
}
.reservation-chat-files {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    min-width: 0;
    max-width: 100%;
}
.reservation-chat-file {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-align: left;
}
.reservation-chat-file-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: underline;
}
.reservation-chat-chips {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.reservation-chat-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1 1 0;
    max-width: max-content;
    min-width: 0;
    overflow: hidden;
    padding: 2px 2px 2px 8px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.08);
}
.reservation-chat-chip-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8rem;
}
.reservation-chat-chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    margin-left: 2px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;
}
.reservation-chat-chip-remove:hover {
    background: rgba(var(--v-theme-on-surface), 0.12);
}
.reservation-chat-meta {
    margin-top: 4px;
    font-size: 0.7rem;
    line-height: 1.2;
    opacity: 0.65;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
}
.reservation-chat-row--mine .reservation-chat-meta {
    justify-content: flex-end;
}
.reservation-chat-meta-who {
    max-width: 14rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
    .reservation-chat-collapse,
    .reservation-chat-panel,
    .reservation-chat-layer--enlarged {
        transition: none;
        animation: none;
    }
}
</style>
